import { makeUpfileInputFragmentV2 } from "@nijiurachan/js/components/upfile-input-fragment-v2";
import { makeUpfileInputV2Element } from "@nijiurachan/js/elements/upfile-input-v2";
import { AxnosPaintPopup } from "@nijiurachan/js/io/axnos-paint-popup";
import { KlecksPopup } from "@nijiurachan/js/io/klecks-popup";
import { initCookieStore } from "@nijiurachan/js/util/cookie-store";
import {
  createRouter,
  type ParsedLocation,
  type Router,
  RouterProvider,
} from "@tanstack/react-router";
import { type ErrorInfo, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AppErrorFallback } from "@/app/AppErrorBoundary";
import { logAppError } from "@/app/logAppError";
import { getBoardRouterBasepath } from "@/app/routerConfig";
import { BOARD_CONFIG } from "@/config/browserBoard";
import { useNgStore } from "@/features/ng-filter/stores/ngStore";
import { installCanvas98Patch } from "@/features/otegaki-upfile/lib/canvas98Patch";
import { installIosOffsetPatch } from "@/features/otegaki-upfile/lib/iosOffsetPatch";
import { AddedBoardsSync } from "@/features/settings/components/AddedBoardsSync";
import { SettingsStoreContext } from "@/features/settings/hooks";
import { createSettingsStore } from "@/features/settings/stores";
import { filterThreadForSnapshot } from "@/features/thread/utils/aimogeThreadFilter";
import { routeTree } from "@/routeTree.gen";
import { initializeAimoge, setAimogeThreadFilter } from "@/shared/lib/aimoge";
import {
  discardInitialThreadHtmlForNavigation,
  retainInitialThreadHtml,
} from "@/shared/lib/initialThreadHtml";
import { isThreadRoutePath } from "@/shared/lib/threadRoutePath";
import { ComponentRoot } from "@/shared/ui/component-hierarchy/ComponentRoot";

// これがないとViteでUpfileInputがうまく動かない
import "preact";

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: Router<typeof routeTree>;
  }
}

if (!window.cookieStore) {
  await initCookieStore();
}

const settingsStore: ReturnType<typeof createSettingsStore> =
  createSettingsStore();

initUpfileInput(settingsStore);
initializeAimoge();
installAimogeThreadNgFilter();

const rootElement = document.getElementById("root") as HTMLElement;
// SSR本文をJS有効時に可視化するとSPA本文描画時にちらつきが発生するため、
// 初期HTMLは保持せず即削除する。JS無効環境はthreadRendererのnoscript CSSで救済される。
retainInitialThreadHtml(rootElement, false);
createRoot(rootElement).render(makeRoot(settingsStore));

/** ルート要素を作成 */
function makeRoot(
  settingsStore: ReturnType<typeof createSettingsStore>,
): React.ReactElement {
  const router = createRouter({
    routeTree,
    scrollRestoration: true,
    basepath: getBoardRouterBasepath(BOARD_CONFIG),
    // defaultPreload: "intent" だけがルートチャンクの先読みに効く。ルート
    // loaderがリポジトリ内に存在しないため、defaultPreloadStaleTimeは
    // 効果を持たない(=削除済み)。データの先読みはカタログhover側の
    // usePrefetchThreadState(TanStack Query prefetchQuery)が別途担う。
    defaultPreload: "intent",
    defaultErrorComponent: AppErrorFallback,
    defaultOnCatch: (error: unknown, errorInfo: ErrorInfo): void =>
      logAppError(error, errorInfo),
    getScrollRestorationKey: ({ pathname, state }: ParsedLocation) =>
      shouldRestoreScrollAt(pathname) ? pathname : (state.__TSR_key as string),
  });
  router.subscribe("onBeforeNavigate", ({ fromLocation, toLocation }) => {
    discardInitialThreadHtmlForNavigation(fromLocation?.href, toLocation.href);
  });

  return (
    <StrictMode>
      <ComponentRoot>
        <AddedBoardsSync />
        <SettingsStoreContext value={settingsStore}>
          <RouterProvider router={router} />
        </SettingsStoreContext>
      </ComponentRoot>
    </StrictMode>
  );
}

/** スクロールを記憶してほしいページかどうか判定 */
function shouldRestoreScrollAt(pathname: string): boolean {
  return pathname === "/" || isThreadRoutePath(pathname);
}

/** 添付ファイル欄を初期化 */
function initUpfileInput(
  settingsStore: ReturnType<typeof createSettingsStore>,
): void {
  // ポップアップ用のJSパスはimportmapから取得する(Viteの設定参照)
  const paintPopupUrl = import.meta.resolve("#oekaki");
  const klecksPopupUrl = import.meta.resolve("#klecks");
  const klecksEmbedUrl =
    import.meta.env.VITE_KLECKS_EMBED_URL?.trim() ||
    import.meta.resolve("#klecks-embed");

  makeUpfileInputV2Element(
    makeUpfileInputFragmentV2({
      axnos: new AxnosPaintPopup(paintPopupUrl),
      klecks: new KlecksPopup(klecksPopupUrl, klecksEmbedUrl),
      getOekakiTool: () => settingsStore.getState().oekakiTool,
    }),
  ).define();

  // はっちゃんのスマホ向け上書き
  installIosOffsetPatch();
  installCanvas98Patch();
}

/**
 * 拡張向け snapshot の `getThread({ ng: true })` を有効化する。
 * shared→features の層違反を避けるため ng-filter は shared/lib/aimoge から
 * 参照せず、ここで注入する。NG設定変更のたびに、その時点の isPostHidden を
 * 束ねた新しい関数参照を差し込み、内部キャッシュを破棄させる。
 */
function installAimogeThreadNgFilter(): void {
  const push = (): void => {
    const { isPostHidden } = useNgStore.getState();
    setAimogeThreadFilter((thread) =>
      filterThreadForSnapshot(thread, isPostHidden),
    );
  };
  push();
  useNgStore.subscribe(push);
}
